/**
 * The one keyboard menu of the kit (WAI-ARIA menu) and its two openers:
 *
 *  · MenuButton — a menu button: the DataGrid « Colonnes » menu and « Plus d'actions » row menu,
 *    the PageHeader « Plus… » overflow and any screen verb list (ux/01 §3.5, §3.13);
 *  · ContextMenu — the same menu at the pointer of a right-click or at the active row (Maj+F10 /
 *    the menu key), opened by DataGrid `contextActions` (refonte WP-100, CP §15.6, §11).
 *
 * Both render MenuSurface; the key and placement rules live in lib/menu.js.
 *
 * Keyboard: on the trigger Entrée / Espace / ↓ open on the first item, ↑ on the last · in the menu
 * ↑ / ↓ move (wrapping), Début / Fin, a letter jumps to the next item starting with it, Entrée /
 * Espace activate, Échap closes and returns the focus to the opener — the menu owns Échap while it
 * is open (EXE-73: an Échap bound by the screen or the drawer behind never fires with it). A menu
 * button closes on Tab; a context menu keeps the focus (Tab / Maj+Tab move) and lets it land on a
 * disabled item so its reason is read. A `checked` item is a menuitemcheckbox and keeps the menu
 * open when toggled.
 * a11y: trigger aria-haspopup="menu" + aria-expanded + aria-controls; the list is role="menu"
 * labelled by the trigger (a context menu by its own label). The popup is portaled with fixed
 * positioning so a scrolling grid never clips it.
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Button, Icon, IconButton, type ButtonProps } from "./primitives";
import { contextMenuPosition, menuEdge, menuKeyAction, menuStep, menuTypeahead } from "@/lib/menu.js";
import { anchorStyle, useAnchorPosition } from "@/hooks/useAnchorPosition.js";
import { useShortcuts } from "@/hooks/useShortcuts.js";
import { GLOBAL_SHORTCUTS } from "@/config/shortcuts.config.js";

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  onSelect?: () => void;
  disabled?: boolean;
  /** Present → menuitemcheckbox (the menu stays open when toggled). */
  checked?: boolean;
  danger?: boolean;
  /** Key chip shown at the end of the item (« Suppr »). */
  shortcut?: string;
  /** Explains why an item is disabled (read with the item). */
  hint?: string;
  /** A separator line above the item (groups of a long menu). */
  separatorBefore?: boolean;
}

/** While a menu is open its Échap is the registry's « Fermer » (an overlay scope, innermost). */
const MENU_KEYS = Object.freeze(GLOBAL_SHORTCUTS.filter((b) => b.id === "FERMER"));

export interface MenuSurfaceProps {
  items: MenuItem[];
  active: number;
  onActiveChange: (index: number) => void;
  /** Close the menu; `restore` gives the focus back to the opener. */
  onClose: (restore: boolean) => void;
  id?: string;
  labelledBy?: string;
  ariaLabel?: string;
  heading?: string | null;
  header?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Tab / Maj+Tab move inside the menu instead of closing it (context menu). */
  trapTab?: boolean;
  /** The focus may land on disabled items (their reason is read); Entrée does nothing there. */
  includeDisabled?: boolean;
  /** A pointer press on this element does not close the menu (the trigger toggles it itself). */
  ignoreRef?: React.RefObject<HTMLElement | null>;
  surfaceRef?: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * The role="menu" list both openers render: items, keys, outside press, Échap ownership. Internal
 * to the kit — exported for the server-rendered contract tests only.
 */
export function MenuSurface({
  items,
  active,
  onActiveChange,
  onClose,
  id,
  labelledBy,
  ariaLabel,
  heading,
  header,
  className,
  style,
  trapTab = false,
  includeDisabled = false,
  ignoreRef,
  surfaceRef,
}: MenuSurfaceProps) {
  const ownRef = React.useRef<HTMLDivElement | null>(null);
  const ref = surfaceRef ?? ownRef;
  const opts = { includeDisabled };

  useShortcuts("MENU_OUVERT", { FERMER: () => onClose(true) }, { kind: "overlay", entries: MENU_KEYS, title: "Menu" });

  React.useEffect(() => {
    ref.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.focus({ preventScroll: true });
  }, [active, ref]);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || ignoreRef?.current?.contains(t)) return;
      onClose(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose, ignoreRef, ref]);

  const activate = (it: MenuItem | undefined) => {
    if (!it || it.disabled) return;
    it.onSelect?.();
    if (it.checked === undefined) onClose(true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const action = menuKeyAction(e, { trapTab });
    switch (action) {
      case "next":
      case "prev":
        e.preventDefault();
        onActiveChange(menuStep(items, active, action === "next" ? 1 : -1, opts));
        break;
      case "first":
      case "last":
        e.preventDefault();
        onActiveChange(menuEdge(items, action, opts));
        break;
      case "close":
        // Handled here: an enclosing dialog must not close with the menu.
        e.preventDefault();
        e.stopPropagation();
        onClose(true);
        break;
      case "leave":
        onClose(false);
        break;
      case "activate":
        e.preventDefault();
        activate(items[active]);
        break;
      case "type":
        onActiveChange(menuTypeahead(items, active, e.key, opts));
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={ref}
      id={id}
      role="menu"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : ariaLabel}
      className={cn("pk-menu", className)}
      style={style}
      onKeyDown={onKeyDown}
      // the focus leaves the menu (a dialog opened by a screen key, another window): it closes
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && (ref.current?.contains(next) || ignoreRef?.current?.contains(next))) return;
        onClose(false);
      }}
      // a press inside never moves the focus out of the menu (the click still activates the item)
      onMouseDown={(e) => e.preventDefault()}
      // no browser menu over the menu (a keyboard menu key pressed while it is open lands here)
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {header && (
        <div className="pk-menu-header" role="presentation">
          {header}
        </div>
      )}
      {heading && (
        <div className="pk-menu-heading" role="presentation">
          {heading}
        </div>
      )}
      {items.map((it, i) => (
        <React.Fragment key={it.id}>
          {it.separatorBefore && i > 0 && <div role="separator" className="pk-menu-sep" />}
          <div
            data-index={i}
            data-menu-item={it.id}
            role={it.checked === undefined ? "menuitem" : "menuitemcheckbox"}
            aria-checked={it.checked === undefined ? undefined : it.checked}
            aria-disabled={it.disabled || undefined}
            tabIndex={i === active ? 0 : -1}
            className={cn(
              "pk-menu-item",
              it.danger && "pk-menu-item--danger",
              it.disabled && "pk-menu-item--disabled",
            )}
            onClick={() => activate(it)}
            onMouseMove={() => {
              if ((includeDisabled || !it.disabled) && i !== active) onActiveChange(i);
            }}
          >
            {it.checked !== undefined ? (
              <span className="pk-menu-check" aria-hidden="true">
                {it.checked && <Icon name="check" size={14} />}
              </span>
            ) : it.icon ? (
              <Icon name={it.icon} size={16} aria-hidden="true" />
            ) : null}
            <span className="pk-menu-label">
              {it.label}
              {it.hint && <span className="pk-menu-hint">{it.hint}</span>}
            </span>
            {it.shortcut && <kbd className="pk-kbd">{it.shortcut}</kbd>}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export interface MenuButtonHandle {
  open: (focus?: "first" | "last") => void;
  close: () => void;
}

export interface MenuButtonProps {
  items: MenuItem[];
  /** Trigger label (text button) — or pass `icon` + `iconOnly` for an IconButton. */
  label: string;
  icon?: string;
  iconOnly?: boolean;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  align?: "start" | "end";
  /** Optional heading inside the menu (« Colonnes affichées »). */
  heading?: string;
  className?: string;
  triggerProps?: Record<string, unknown>;
  tabIndex?: number;
  /**
   * Custom trigger content (the topbar user menu: avatar, name, role) —
   * rendered in a plain button that keeps the menu-button ARIA and keys;
   * `label` then names it for assistive technologies.
   */
  triggerContent?: React.ReactNode;
  /** A presentational block at the top of the menu (identity, context). */
  header?: React.ReactNode;
  /** Extra class on the menu surface. */
  menuClassName?: string;
}

export const MenuButton = React.forwardRef<MenuButtonHandle, MenuButtonProps>(
  (
    {
      items,
      label,
      icon,
      iconOnly,
      variant = "secondary",
      size = "sm",
      align = "start",
      heading,
      className,
      triggerProps,
      tabIndex,
      triggerContent,
      header,
      menuClassName,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [active, setActive] = React.useState(0);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const pos = useAnchorPosition(triggerRef, open, { align });
    const menuId = React.useId();
    const triggerId = React.useId();

    const openMenu = React.useCallback(
      (focus: "first" | "last" = "first") => {
        setActive(menuEdge(items, focus));
        setOpen(true);
      },
      [items],
    );
    const close = React.useCallback((restore = true) => {
      setOpen(false);
      if (restore) triggerRef.current?.focus();
    }, []);
    React.useImperativeHandle(ref, () => ({ open: openMenu, close: () => close(true) }), [
      openMenu,
      close,
    ]);

    const onTriggerKey = (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openMenu("first");
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        openMenu("last");
      }
    };

    const common = {
      ref: triggerRef,
      id: triggerId,
      "aria-haspopup": "menu" as const,
      "aria-expanded": open,
      "aria-controls": open ? menuId : undefined,
      onClick: () => (open ? close(true) : openMenu("first")),
      onKeyDown: onTriggerKey,
      tabIndex,
      ...(triggerProps ?? {}),
    };

    return (
      <>
        {triggerContent ? (
          <button type="button" {...common} aria-label={label} className={className}>
            {triggerContent}
          </button>
        ) : iconOnly ? (
          <IconButton
            {...common}
            icon={icon ?? "more-horizontal"}
            label={label}
            size={size === "lg" ? "lg" : size === "md" ? "md" : "sm"}
          />
        ) : (
          <Button {...common} variant={variant} size={size} icon={icon} className={className}>
            {label}
            <Icon name="chevron-down" size={14} aria-hidden="true" />
          </Button>
        )}
        {open &&
          pos &&
          createPortal(
            <MenuSurface
              id={menuId}
              labelledBy={triggerId}
              items={items}
              active={active}
              onActiveChange={setActive}
              onClose={close}
              heading={heading}
              header={header}
              className={menuClassName}
              style={anchorStyle(pos)}
              ignoreRef={triggerRef}
            />,
            document.body,
          )}
      </>
    );
  },
);
MenuButton.displayName = "MenuButton";

/** Where a context menu opens: the pointer, or the active row (lib/menu.js `rowMenuAnchor`). */
export interface ContextMenuAnchor {
  x: number;
  y: number;
  /** Top edge of the row the menu is attached to (it goes above the row when there is no room). */
  top?: number;
  fromKeyboard?: boolean;
}

export interface ContextMenuProps {
  items: MenuItem[];
  anchor: ContextMenuAnchor;
  /** Accessible name of the menu (« Actions sur les 3 lignes sélectionnées »). */
  label: string;
  /** The target, shown at the top of the menu (« 3 lignes sélectionnées », the row's name). */
  heading?: string | null;
  /** `restore`: the caller gives the focus back to its row. */
  onClose: (restore: boolean) => void;
  className?: string;
}

/**
 * A menu at a point (refonte WP-100): opened by DataGrid on a right-click or Maj+F10 / the menu key.
 * The focus starts on the first enabled item and stays in the menu; a scroll or a resize closes it
 * (the anchor no longer holds).
 */
export function ContextMenu({ items, anchor, label, heading, onClose, className }: ContextMenuProps) {
  const surfaceRef = React.useRef<HTMLDivElement | null>(null);
  const [active, setActive] = React.useState(() => {
    const i = menuEdge(items, "first");
    return i >= 0 ? i : Math.max(0, menuEdge(items, "first", { includeDisabled: true }));
  });
  const [placed, setPlaced] = React.useState<{ top: number; left: number; maxHeight: number } | null>(null);

  React.useLayoutEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPlaced(
      contextMenuPosition(
        anchor,
        { width: r.width, height: Math.max(r.height, el.scrollHeight) },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [anchor]);

  React.useEffect(() => {
    const onScroll = (e: Event) => {
      if (surfaceRef.current?.contains(e.target as Node)) return;
      onClose(true);
    };
    const onResize = () => onClose(true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [onClose]);

  return createPortal(
    <MenuSurface
      surfaceRef={surfaceRef}
      ariaLabel={label}
      items={items}
      active={active}
      onActiveChange={setActive}
      onClose={onClose}
      heading={heading}
      className={cn("pk-menu--context", className)}
      style={placed ?? { top: anchor.y, left: anchor.x }}
      trapTab
      includeDisabled
    />,
    document.body,
  );
}
